import React, { useState, useEffect } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import API_CONFIG from '../config/api';
import { CreditsData, ChartDataItem, CreditDistributionItem } from '../types/api';
import { requestManager } from '../utils/requestManager';
import { cacheEventManager } from '../utils/cacheEventManager';

interface CreditsProps {
    timeRange: string;
}

const Credits: React.FC<CreditsProps> = ({ timeRange }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [creditsData, setCreditsData] = useState<CreditsData | null>(null);

    // State for collapsible sections
    const [collapsedSections, setCollapsedSections] = useState<{ [key: string]: boolean }>({
        charts: false,
        creditDistribution: false
    });

    // Toggle function for collapsible sections
    const toggleSection = (sectionKey: string) => {
        setCollapsedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    // API Helper Function with request deduplication
    const apiCall = async (endpoint: string) => {
        try {
            const url = `${API_CONFIG.BASE_URL}${endpoint}?period=${timeRange}`;
            const data = await requestManager.makeRequest(url);

            if (!data.success) throw new Error(data.message || 'API call failed');
            return data.data;
        } catch (error) {
            console.error(`API Error for ${endpoint}:`, error);
            throw error;
        }
    };

    const fetchCreditsData = async () => {
        try {
            setLoading(true);
            const data = await apiCall(API_CONFIG.ENDPOINTS.ANALYTICS.CREDITS);
            setCreditsData(data);
        } catch (error) {
            console.error('Error fetching credits data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCreditsData();
    }, [timeRange]);

    // Listen for cache clear events to auto-refresh data
    useEffect(() => {
        const unsubscribe = cacheEventManager.subscribe(() => {
            fetchCreditsData();
        });

        return unsubscribe;
    }, [timeRange]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading credits data...</p>
                </div>
            </div>
        );
    }

    const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

    // Prepare data for charts
    const chartData = creditsData ? [
        { name: 'Current Credits', value: creditsData.total_current_credits || 0, color: '#3B82F6' },
        { name: 'Distributed', value: creditsData.total_credits_distributed || 0, color: '#10B981' },
        { name: 'Used', value: creditsData.total_credits_used || 0, color: '#F59E0B' },
        { name: 'Net Change', value: Math.abs(creditsData.net_credit_change || 0), color: (creditsData.net_credit_change || 0) >= 0 ? '#10B981' : '#EF4444' }
    ].filter(item => item.value > 0) : [];

    // Prepare data for pie chart (only Distributed and Used)
    const pieChartData = creditsData ? [
        { name: 'Total Distributed', value: creditsData.total_credits_distributed || 0, color: '#10B981' },
        { name: 'Total Used', value: creditsData.total_credits_used || 0, color: '#F59E0B' }
    ].filter(item => item.value > 0) : [];

    const usageRate = creditsData ?
        ((creditsData.total_credits_used || 0) / (creditsData.total_credits_distributed || 1) * 100).toFixed(1) : 0;

    const currentCreditsRate = creditsData ?
        ((creditsData.total_current_credits || 0) / (creditsData.total_credits_distributed || 1) * 100).toFixed(1) : 0;

    // Calculate pie chart percentages
    const totalPieValue = (creditsData?.total_credits_distributed || 0) + (creditsData?.total_credits_used || 0);
    const distributedPiePercentage = totalPieValue > 0 ?
        (((creditsData?.total_credits_distributed || 0) / totalPieValue) * 100).toFixed(1) : 0;
    const usedPiePercentage = totalPieValue > 0 ?
        (((creditsData?.total_credits_used || 0) / totalPieValue) * 100).toFixed(1) : 0;

    // Collapsible Header Component
    const CollapsibleHeader: React.FC<{
        title: string;
        count?: number;
        color: string;
        isCollapsed: boolean;
        onToggle: () => void;
    }> = ({ title, count, color, isCollapsed, onToggle }) => (
        <div
            className="flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2 -m-2 transition-colors"
            onClick={onToggle}
        >
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center">
                <div className={`w-4 h-4 ${color} rounded-full mr-2`}></div>
                {title} {count !== undefined && `(${count})`}
            </h3>
            <div className="flex items-center">
                <svg
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''
                        }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>
        </div>
    );

    return (
        <div className="flex-1 flex flex-col min-h-0">
            {/* Content */}
            <main className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
                {/* Summary Cards - Single Row Layout */}
                <div className="grid grid-cols-4 gap-4 mb-8">
                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Current Credits</p>
                        <p className="text-2xl font-bold text-blue-600 mt-1">
                            {creditsData?.total_current_credits?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Total Distributed</p>
                        <p className="text-2xl font-bold text-green-600 mt-1">
                            {creditsData?.total_credits_distributed?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Total Used</p>
                        <p className="text-2xl font-bold text-orange-600 mt-1">
                            {creditsData?.total_credits_used?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Net Credit Change</p>
                        <p className={`text-2xl font-bold mt-1 ${(creditsData?.net_credit_change || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {(creditsData?.net_credit_change || 0) >= 0 ? '+' : ''}{creditsData?.net_credit_change?.toLocaleString() || 0}
                        </p>
                    </div>
                </div>

                {/* Charts - Side by Side Layout */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="Credits Charts"
                        isCollapsed={collapsedSections.charts}
                        onToggle={() => toggleSection('charts')}
                        color="bg-orange-500"
                    />

                    {!collapsedSections.charts && (
                        <div className="flex gap-6 mt-6">
                            {/* Credits Distribution Pie Chart - Left Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4">Credits Distribution</h4>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={pieChartData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={120}
                                                paddingAngle={5}
                                                dataKey="value"
                                                label={({ name, value, percent }) => `${name}: ${value.toLocaleString()} (${(percent * 100).toFixed(1)}%)`}
                                            >
                                                {pieChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                formatter={(value, name) => [value.toLocaleString(), name]}
                                                contentStyle={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Usage Bar Chart - Right Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4">Credits Usage Overview</h4>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis
                                                dataKey="name"
                                                tick={{ fontSize: 12 }}
                                            />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip
                                                formatter={(value, name) => [value.toLocaleString(), 'Credits']}
                                                contentStyle={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                            <Bar dataKey="value">
                                                {chartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    )}
                </div>


                {/* Credit Distribution Data */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="Credit Distribution Data"
                        count={creditsData?.credit_distribution_data?.length || 0}
                        isCollapsed={collapsedSections.creditDistribution}
                        onToggle={() => toggleSection('creditDistribution')}
                        color="bg-orange-500"
                    />

                    {!collapsedSections.creditDistribution && (
                        <div className="mt-6">
                            {creditsData?.credit_distribution_data && creditsData.credit_distribution_data.length > 0 ? (
                                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    User
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Credits Given
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Date
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200/60 dark:divide-white/10">
                                            {creditsData.credit_distribution_data
                                                .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
                                                .map((item, index) => (
                                                    <tr key={item.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                <div className="flex-shrink-0 h-10 w-10">
                                                                    <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-semibold text-sm">
                                                                        {item.name.charAt(0).toUpperCase()}
                                                                    </div>
                                                                </div>
                                                                <div className="ml-4">
                                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                        {item.name}
                                                                    </div>
                                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                        {item.user_id}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                            {item.email}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                                {(item.credit_amount || 0).toLocaleString()}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                            {item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            }) : '-'}
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="text-gray-500 dark:text-gray-400">
                                        No credit distribution data available for the selected period.
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </main >
        </div >
    );
};

export default Credits;